import React, { useState, useEffect, useRef } from 'react';
import { Brain, Zap, Layers, Code, Database, Cpu, GitBranch } from 'lucide-react';
import { portfolioData } from '../data/mock';

const categoryIcons = {
  'AI Engineering': Brain,
  'Automation Engineering': Zap,
  'Platform Engineering': Layers,
  'Backend Engineering': Code,
  'Data Engineering': Database,
  'Distributed Systems': Cpu
};

const Capabilities = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="capabilities" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/5 to-transparent" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <GitBranch size={12} />
              <span>Engineering Matrix</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Engineering <span className="gradient-text">Capabilities</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Multi-domain expertise spanning AI, automation, infrastructure, and distributed systems</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {portfolioData.capabilities.map((category, idx) => {
              const Icon = categoryIcons[category.category] || Code;
              return (
                <div key={category.category} className="glass rounded-2xl p-6 sm:p-8 hover:border-white/10 transition-all duration-500 group"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${idx * 100}ms`,
                    transitionProperty: 'all',
                    transitionDuration: '0.6s',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                  }}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="text-white font-semibold text-lg">{category.category}</h3>
                  </div>
                  <div className="space-y-2.5">
                    {category.items.map((skill) => (
                      <div key={skill} className="flex items-center space-x-2.5">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${category.color}`} />
                        <span className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
